import React, { useCallback, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { ScrollView, type ScrollViewInstance, StyleSheet } from 'react-native';
import { type Edges, SafeAreaView } from 'react-native-safe-area-context';

import { theme } from '@garnish/constants';
import { useKeyboardAvoiderPadding, useResponsive } from '@garnish/core';
import { Form } from '@garnish/forms';
import { useNoticeBannersStackActions } from '@garnish/overlays';

import { type Address, type DeliveryPreferenceType } from '@sg/graphql-schema';

import { useTrackEffect } from '@order/Telemetry';

import {
  type AddressForm as AddressFormType,
  type AddressType,
  defaultAddressForm,
} from './address.config';
import {
  AddressFormAddressType,
  AddressFormCustomNameField,
  AddressFormDeliveryNotes,
  AddressFormDeliveryPreference,
  AddressFormFooter,
  AddressFormPredictions,
} from './components';
import { useAddressDuplicateByLocation, useAddressDuplicateByName, useAddressForm } from './hooks';
import { getAddressTypeFromAddress, getAvailableAddress } from './utils';

import type { MinimumAddress } from '../../../locations.types';

export const AddressForm = (props: AddressFormProps) => {
  const {
    savedCustomerAddress,
    customerAddresses,
    visible,
    editing,
    editingAddressId,
    disableAddress,
    onSubmit,
    onDelete,
    isLoading,
    isDeleting,
    submitCtaOverride,
    isLiquidGlassEnabled,
    onBackPress,
  } = props;

  const { formatMessage } = useIntl();
  const { currentBreakpoint } = useResponsive();
  const { push: addNoticeBanner } = useNoticeBannersStackActions();
  const scrollViewRef = useRef<ScrollViewInstance>(null);
  const { keyboardPadding } = useKeyboardAvoiderPadding();

  // ─── Telemetry ───────────────────────────────────────────────────────

  useTrackEffect(editing ? 'addresses.view' : 'addresses.add');

  // ─── Default Values ──────────────────────────────────────────────────

  const defaultValues = useMemo(() => {
    const typeBeingEdited = getAddressTypeFromAddress(savedCustomerAddress);
    const availableAddress = getAvailableAddress(customerAddresses);
    const addressType = savedCustomerAddress?.name ? typeBeingEdited : availableAddress.type;
    const addressName = savedCustomerAddress?.name || availableAddress.name;

    return {
      addressType,
      name: addressName,
      street: savedCustomerAddress?.street ?? defaultAddressForm.street,
      secondaryStreet: savedCustomerAddress?.secondaryStreet ?? defaultAddressForm.secondaryStreet,
      city: savedCustomerAddress?.city ?? defaultAddressForm.city,
      zipCode: savedCustomerAddress?.zipCode ?? defaultAddressForm.zipCode,
      state: savedCustomerAddress?.state ?? defaultAddressForm.state,
      country: savedCustomerAddress?.country ?? defaultAddressForm.country,
      latitude: savedCustomerAddress?.latitude ?? defaultAddressForm.latitude,
      longitude: savedCustomerAddress?.longitude ?? defaultAddressForm.longitude,
      notes: savedCustomerAddress?.notes ?? defaultAddressForm.notes,
      deliveryPreference:
        savedCustomerAddress?.deliveryPreference ?? defaultAddressForm.deliveryPreference,
    };
  }, [savedCustomerAddress, customerAddresses]);

  // ─── Form ────────────────────────────────────────────────────────────

  const onSubmitSuccess = useCallback(
    (formData: AddressFormType) => {
      onSubmit(formData);
    },
    [onSubmit],
  );

  const { form, submitForm } = useAddressForm({
    defaultValues,
    onSubmitSuccess,
  });

  const { control, setValue, watch, formState } = form;
  const formValues = watch();
  const { addressType, name = '', notes, zipCode } = formValues;

  // ─── Selected Address ───────────────────────────────────────────────

  const [selectedAddress, setSelectedAddress] = useState<MinimumAddress | undefined>();

  // ─── Form Validation ─────────────────────────────────────────────────

  const isFormChanged = Boolean(formState.isDirty);
  const isCustomNameTouched = Boolean(formState.dirtyFields.name);
  const isAddressTouched = Boolean(formState.dirtyFields.street);

  const isValidStreet =
    Boolean(selectedAddress) || (!isAddressTouched && Boolean(savedCustomerAddress?.id));
  const hasValidAddressName = addressType !== 'custom' || Boolean(name);
  const isZipCodeMissing = !zipCode;

  // ─── Duplicate Checks ───────────────────────────────────────────────

  const addressId = editingAddressId ?? savedCustomerAddress?.id;

  const isAddressDuplicateByLocation = Boolean(
    useAddressDuplicateByLocation({
      addressId,
      addressForm: formValues,
      addresses: customerAddresses,
    }),
  );

  const { hasHomeAddress, hasWorkAddress, isCustomNameInvalid, customNameNotice } =
    useAddressDuplicateByName({
      addressId,
      addressName: name,
      isCustomNameTouched,
      addresses: customerAddresses,
    });

  // ─── Callbacks ──────────────────────────────────────────────────────

  const handleDeliveryPreference = useCallback(
    (preference: DeliveryPreferenceType) => {
      setValue('deliveryPreference', preference, { shouldDirty: true });
    },
    [setValue],
  );

  const handleAddressTypeChange = useCallback(
    (type: AddressType) => {
      const validName = ['home', 'work'].includes(type) ? type : '';

      setValue('name', validName, { shouldDirty: true });
      setValue('addressType', type, { shouldDirty: true });
    },
    [setValue],
  );

  const handleNameUpdate = useCallback(
    (name: string) => {
      setValue('name', name, { shouldDirty: true });
    },
    [setValue],
  );

  const handleSubmit = useCallback(() => {
    if (isAddressDuplicateByLocation) {
      addNoticeBanner({
        text: formatMessage(messages.duplicateAddressLocation),
        palette: 'caution',
      });

      return;
    }

    submitForm(formValues);
  }, [formValues, isAddressDuplicateByLocation, formatMessage, addNoticeBanner, submitForm]);

  // ─── Submit Control ─────────────────────────────────────────────────

  const preventSubmittingForUnchangedForm = Boolean(savedCustomerAddress?.name) && !isFormChanged;
  const preventSubmittingForInvalidFields = isCustomNameInvalid || isZipCodeMissing;
  const canSubmit =
    Boolean(addressType) &&
    isValidStreet &&
    hasValidAddressName &&
    !preventSubmittingForUnchangedForm &&
    !preventSubmittingForInvalidFields;

  // ─── Render ─────────────────────────────────────────────────────────

  return (
    <SafeAreaView
      edges={currentBreakpoint.isXS ? BOTTOM_SAFE_AREA_EDGES : NO_SAFE_AREA_EDGES}
      style={[styles.container, keyboardPadding]}
    >
      <ScrollView ref={scrollViewRef} contentContainerStyle={styles.content}>
        <AddressFormPredictions
          savedCustomerAddress={savedCustomerAddress}
          selectedAddress={selectedAddress}
          visible={visible}
          disabled={disableAddress}
          setValue={setValue}
          setSelectedAddress={setSelectedAddress}
          accessibilityLabel={formatMessage(messages.streetA11y)}
        />

        <Form.TextField
          name="secondaryStreet"
          control={control}
          accessibilityLabel={formatMessage(messages.secondaryStreetA11y)}
          label={formatMessage(messages.secondaryStreetPlaceholder)}
        />

        <AddressFormDeliveryPreference
          value={formValues.deliveryPreference}
          onChange={handleDeliveryPreference}
        />

        <AddressFormDeliveryNotes form={form} notes={notes} maxLength={128} />

        <AddressFormAddressType
          addressType={addressType}
          hasHomeAddress={hasHomeAddress}
          hasWorkAddress={hasWorkAddress}
          onChange={handleAddressTypeChange}
        />

        {addressType === 'custom' ? (
          <AddressFormCustomNameField
            name={name}
            scrollViewRef={scrollViewRef}
            customNameNotice={customNameNotice}
            isCustomNameInvalid={isCustomNameInvalid}
            onChangeText={handleNameUpdate}
          />
        ) : null}
      </ScrollView>

      <AddressFormFooter
        canSubmit={canSubmit}
        isLoading={isLoading}
        onSubmit={handleSubmit}
        canDelete={Boolean(editing && onDelete)}
        isDeleting={isDeleting}
        onDelete={onDelete}
        submitCtaOverride={submitCtaOverride}
        isLiquidGlassEnabled={isLiquidGlassEnabled}
        onBackPress={onBackPress}
      />
    </SafeAreaView>
  );
};

// ─── Messages ──────────────────────────────────────────────────────────────

const messages = defineMessages({
  streetA11y: {
    defaultMessage: 'Delivery address',
    description: 'Address Form | Street a11y label',
  },
  secondaryStreetA11y: {
    defaultMessage: 'Secondary street',
    description: 'Address Form | Secondary street a11y label',
  },
  secondaryStreetPlaceholder: {
    defaultMessage: 'Apt number, suite, company',
    description: 'Address Form | Secondary street placeholder',
  },
  duplicateAddressLocation: {
    defaultMessage: 'You already have this address on file',
    description: 'Address Form | Duplicate address location error',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const BOTTOM_SAFE_AREA_EDGES: Edges = ['bottom'];
const NO_SAFE_AREA_EDGES: Edges = [];

// ─── Styles ────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    flexShrink: 1,
    justifyContent: 'space-between',
  },
  content: {
    flexGrow: 1,
    paddingTop: theme.spacing['2'],
    paddingBottom: theme.spacing['6'],
    paddingHorizontal: theme.spacing['6'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

export type AddressFormProps = {
  savedCustomerAddress?: Partial<Address>;
  customerAddresses: ReadonlyArray<Partial<Address>>;
  visible: boolean;
  onSubmit: (formData: AddressFormType) => void;
  onDelete?: () => void;
  isLoading?: boolean;
  isDeleting?: boolean;
  editing?: boolean;
  editingAddressId?: string;
  disableAddress?: boolean;
  submitCtaOverride?: string;
  isLiquidGlassEnabled?: boolean;
  onBackPress?: () => void;
};
